import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Gift, Copy, MessageCircle, Mail, Loader2, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";

/**
 * CreateGiftTicketDialog — shared by guest (BookingResumePage) and admin
 * (AdminDashboardPage). Lets the user pick a style, write a personal
 * message and a recipient name, then returns a shareable public URL.
 *
 * Props:
 *  - open / onOpenChange
 *  - bookingCode: string  (required)
 *  - existingGift: object (optional — pre-fill if already created)
 *  - onCreated: ({ token, url, gift_ticket }) => void
 */
export default function CreateGiftTicketDialog({ open, onOpenChange, bookingCode, existingGift, defaultSender, onCreated }) {
  const [recipient, setRecipient] = useState(existingGift?.recipient_name || "");
  const [message, setMessage] = useState(existingGift?.message || "");
  const [style, setStyle] = useState(existingGift?.style || "boarding");
  const [recipientEmail, setRecipientEmail] = useState(existingGift?.recipient_email || "");
  const [recipientWA, setRecipientWA] = useState(existingGift?.recipient_whatsapp || "");
  const [fromName, setFromName] = useState(existingGift?.from_name || defaultSender || "");
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [result, setResult] = useState(existingGift?.token ? {
    token: existingGift.token,
    url: `${window.location.origin}/gift/${existingGift.token}`,
    gift_ticket: existingGift,
  } : null);
  const [copied, setCopied] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleGenerate = async () => {
    if (!bookingCode) return;
    setLoading(true);
    try {
      const res = await api.post(`/bookings/${bookingCode}/gift-ticket`, {
        recipient_name: recipient.trim(),
        message: message.trim(),
        style,
        recipient_email: recipientEmail.trim(),
        recipient_whatsapp: recipientWA.trim(),
        from_name: fromName.trim(),
      });
      const localUrl = `${window.location.origin}/gift/${res.data.token}`;
      const enriched = { ...res.data, url: localUrl };
      setResult(enriched);
      if (onCreated) onCreated(enriched);
      toast.success("Ticket regalo creato");
    } catch (err) {
      toast.error(errMsg(err, "Errore durante la creazione"));
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!recipientEmail.trim()) {
      toast.error("Inserisci prima l'email del destinatario");
      return;
    }
    setSendingEmail(true);
    try {
      await api.post(`/bookings/${bookingCode}/gift-ticket/send-email`, {
        to_email: recipientEmail.trim(),
        from_name: fromName.trim(),
      });
      setEmailSent(true);
      setTimeout(() => setEmailSent(false), 4000);
      toast.success(`Email inviata a ${recipientEmail.trim()}`);
    } catch (err) {
      toast.error(errMsg(err, "Errore invio email"));
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.url) return;
    try {
      await navigator.clipboard.writeText(result.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Link copiato");
    } catch {
      toast.error("Impossibile copiare. Copia manualmente il link.");
    }
  };

  // Normalise the recipient phone number for wa.me. Stripping anything
  // that isn't a digit is not enough: WhatsApp interprets `wa.me/<n>`
  // as a FULL international number — so a bare Italian mobile like
  // `3206758840` gets mangled (WhatsApp shows a corrupt "+39 328 758
  // 640" because it tries to parse it without country code).
  //   • Strip "+", spaces, parentheses, "00" international prefix.
  //   • If the resulting digit sequence is 9-10 chars (typical Italian
  //     mobile without country code) → prepend "39".
  //   • If it already starts with a country code → leave it.
  // Anything else passes through (assume the host knows what she's
  // typing for foreign numbers).
  const whatsappTargetNumber = (() => {
    if (!result || !recipientWA) return "";
    let digits = recipientWA.trim().replace(/^\+/, "").replace(/^00/, "").replace(/\D/g, "");
    if (!digits) return "";
    if (digits.length >= 9 && digits.length <= 10 && digits.startsWith("3")) {
      digits = "39" + digits;
    }
    return digits;
  })();
  const whatsappMsg = result ? encodeURIComponent(`Ciao${recipient ? ` ${recipient}` : ""}! Ho un regalo per te 🎁\n\nTi aspetta una vacanza al B&B Emmanuel a Reggio Calabria.\nApri il ticket: ${result.url}`) : "";
  const whatsappDirectUrl = whatsappTargetNumber ? `https://wa.me/${whatsappTargetNumber}?text=${whatsappMsg}` : `https://wa.me/?text=${whatsappMsg}`;
  const emailSubject = encodeURIComponent("🎁 Un regalo per te al B&B Emmanuel");
  const emailBody = result ? encodeURIComponent(`Ciao!\n\nHo un regalo per te: ti aspetta una vacanza al B&B Emmanuel a Reggio Calabria.\n\nApri il ticket: ${result.url}\n\nA presto!`) : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="gift-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#2C2A26]">
            <Gift className="w-5 h-5 text-[#AC5D45]" /> Crea Ticket Regalo
          </DialogTitle>
          <DialogDescription className="text-[#635F59]">
            Genera un link condivisibile con tutti i dettagli della prenotazione (camera, date, indirizzo)
            <strong> senza mostrare il prezzo</strong>. Perfetto per fare un regalo.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-xs font-semibold text-[#2C2A26] tracking-wide uppercase">Stile del ticket</label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setStyle("boarding")}
                  data-testid="gift-style-boarding"
                  className={`border-2 rounded-xl p-3 text-left transition ${style === "boarding" ? "border-[#AC5D45] bg-[#FFF5EE]" : "border-[#E4D5C0] hover:border-[#AC5D45]/60"}`}
                >
                  <div className="text-xl mb-1">🎫</div>
                  <div className="font-semibold text-sm text-[#2C2A26]">Boarding Pass</div>
                  <div className="text-[11px] text-[#635F59]">Elegante, moderno</div>
                </button>
                <button
                  type="button"
                  onClick={() => setStyle("classic")}
                  data-testid="gift-style-classic"
                  className={`border-2 rounded-xl p-3 text-left transition ${style === "classic" ? "border-[#AC5D45] bg-[#FFF5EE]" : "border-[#E4D5C0] hover:border-[#AC5D45]/60"}`}
                >
                  <div className="text-xl mb-1">🎀</div>
                  <div className="font-semibold text-sm text-[#2C2A26]">Tradizionale</div>
                  <div className="text-[11px] text-[#635F59]">Logo + cornice</div>
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-[#2C2A26] tracking-wide uppercase">Nome del destinatario</label>
              <Input
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder="Es. Marco Rossi"
                maxLength={80}
                data-testid="gift-recipient-input"
                className="mt-2"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-[#2C2A26] tracking-wide uppercase">Il tuo nome <span className="text-[#AC5D45]">·</span> <span className="text-[#8A8680] font-normal normal-case">mittente — apparirà sul ticket come "Un regalo da"</span></label>
              <Input
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                placeholder="Es. Luigi Rossi"
                maxLength={80}
                data-testid="gift-from-name-input"
                className="mt-2"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-[#2C2A26] tracking-wide uppercase">Messaggio personalizzato <span className="text-[#8A8680] font-normal lowercase">(opzionale)</span></label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Es. Buon compleanno! Ti aspetta una vacanza al B&B Emmanuel..."
                maxLength={500}
                rows={3}
                data-testid="gift-message-input"
                className="mt-2 resize-none"
              />
              <div className="text-[11px] text-[#8A8680] mt-1 text-right">{message.length}/500</div>
            </div>

            {/* Optional: recipient contact for direct delivery */}
            <div className="bg-[#F5F1EA] border border-[#E4D5C0] rounded-xl p-3 space-y-3">
              <div className="text-[11px] text-[#635F59] flex items-start gap-2">
                <Gift className="w-3.5 h-3.5 mt-0.5 text-[#AC5D45] shrink-0" />
                <span>Vuoi che il sistema invii direttamente il ticket al destinatario? Compila uno o entrambi i campi (opzionale):</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-semibold text-[#2C2A26] tracking-wide uppercase flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Email destinatario
                  </label>
                  <Input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="marco.rossi@email.com"
                    maxLength={120}
                    data-testid="gift-recipient-email-input"
                    className="mt-1.5 h-9 text-sm"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-[#2C2A26] tracking-wide uppercase flex items-center gap-1">
                    <MessageCircle className="w-3 h-3" /> WhatsApp destinatario
                  </label>
                  <Input
                    type="tel"
                    value={recipientWA}
                    onChange={(e) => setRecipientWA(e.target.value)}
                    placeholder="+39 333 1234567"
                    maxLength={30}
                    data-testid="gift-recipient-wa-input"
                    className="mt-1.5 h-9 text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
              <Check className="w-8 h-8 text-emerald-600 mx-auto mb-1" />
              <div className="font-semibold text-emerald-800">Ticket pronto!</div>
              <div className="text-xs text-emerald-700 mt-1">Condividi questo link con chi vuoi:</div>
            </div>

            <div className="flex items-center gap-2 bg-[#F5F1EA] border border-[#E4D5C0] rounded-lg px-3 py-2.5">
              <input
                readOnly
                value={result.url}
                data-testid="gift-result-url"
                className="flex-1 bg-transparent text-sm text-[#2C2A26] font-mono outline-none truncate"
              />
              <Button size="sm" variant="ghost" onClick={handleCopy} data-testid="gift-copy-btn" className="text-[#AC5D45] hover:bg-[#AC5D45]/10">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>

            {/* Direct send — uses the recipient contacts entered earlier */}
            {(recipientEmail || recipientWA) && (
              <div className="bg-emerald-50/60 border border-emerald-200 rounded-xl p-3 space-y-2">
                <div className="text-[11px] font-semibold text-emerald-800 uppercase tracking-wide">Invia direttamente</div>
                {recipientEmail && (
                  <Button
                    onClick={handleSendEmail}
                    disabled={sendingEmail || emailSent}
                    data-testid="gift-send-email-btn"
                    className="w-full bg-[#AC5D45] hover:bg-[#8A4936] text-white"
                  >
                    {sendingEmail ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Invio...</> :
                     emailSent ? <><Check className="w-4 h-4 mr-2" /> Email inviata</> :
                     <><Mail className="w-4 h-4 mr-2" /> Invia email a {recipientEmail}</>}
                  </Button>
                )}
                {recipientWA && (
                  <a href={whatsappDirectUrl} target="_blank" rel="noreferrer" data-testid="gift-send-wa-direct-btn">
                    <Button className="w-full bg-[#25D366] hover:bg-[#1FAA52] text-white">
                      <MessageCircle className="w-4 h-4 mr-2" /> Apri WhatsApp con +{whatsappTargetNumber}
                    </Button>
                  </a>
                )}
              </div>
            )}

            <div className="grid grid-cols-3 gap-2">
              <a href={whatsappDirectUrl} target="_blank" rel="noreferrer" data-testid="gift-share-whatsapp">
                <Button variant="outline" className="w-full border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10">
                  <MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp
                </Button>
              </a>
              <a href={`mailto:${recipientEmail || ""}?subject=${emailSubject}&body=${emailBody}`} data-testid="gift-share-email">
                <Button variant="outline" className="w-full border-[#AC5D45] text-[#AC5D45] hover:bg-[#AC5D45]/10">
                  <Mail className="w-4 h-4 mr-1.5" /> Email
                </Button>
              </a>
              <a href={result.url} target="_blank" rel="noreferrer" data-testid="gift-open-preview">
                <Button variant="outline" className="w-full border-[#635F59] text-[#635F59] hover:bg-[#635F59]/10">
                  <ExternalLink className="w-4 h-4 mr-1.5" /> Anteprima
                </Button>
              </a>
            </div>

            <Button
              variant="ghost"
              onClick={() => setResult(null)}
              data-testid="gift-edit-btn"
              className="w-full text-[#635F59] hover:text-[#AC5D45]"
            >
              Modifica nome/messaggio/stile
            </Button>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="gift-cancel-btn">Annulla</Button>
              <Button
                onClick={handleGenerate}
                disabled={loading}
                data-testid="gift-generate-btn"
                className="bg-[#AC5D45] hover:bg-[#8A4936] text-white"
              >
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Genero...</> : <><Gift className="w-4 h-4 mr-2" /> Genera ticket</>}
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)} data-testid="gift-close-btn" className="bg-[#AC5D45] hover:bg-[#8A4936] text-white w-full">
              Fatto
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
